import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import InputSelect, {
  GroupBase,
  Props,
} from 'react-select'
import {
  withAsyncPaginate,
} from 'react-select-async-paginate'
import {
  twMerge,
} from 'tailwind-merge'

import {
  HelperText,
  Label,
} from '../../Display'
import {
  Components,
} from './components'
import {
  SelectVariants,
  Theme,
} from './Select.theme'
import {
  OmitSelectProps,
  SelectProps,
} from './Select.types'

export function Select<
  Option,
  IsMulty extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
>({
  onChangeValue,
  selectOption = () => ({} as any),
  helperText,
  className,
  selectRef,
  disabled,
  options,
  label,
  error,
  selectKey,
  value,
  variant,
  ...props
}: SelectProps<Option> & Omit<Props<Option, IsMulty, Group>, OmitSelectProps>) {

  const selectOptionRef = useRef(selectOption)
  selectOptionRef.current = selectOption
  const optionsMapRef = useRef(new Map<any, any>())

  const internalSelectRef = useRef<any>(null)
  const hoveredOptionRef = useRef<any>(null)
  const isKeyboardNavigatingRef = useRef<boolean>(false)
  const lastMousePositionRef = useRef<{ x: number; y: number } | null>(null)

  const handleSelectRef = (instance: any) => {
    internalSelectRef.current = instance
    if (typeof selectRef === 'function') {
      selectRef(instance)
    } else if (selectRef && typeof selectRef === 'object') {
      (selectRef as any).current = instance
    }
  }

  const onOptionHoverCustom = useCallback((optionData: any) => {
    isKeyboardNavigatingRef.current = false
    hoveredOptionRef.current = optionData
    const instance = internalSelectRef.current
    if (instance) {
      instance.blockOptionHover = false
      if (typeof instance.onOptionHover === 'function') {
        instance.onOptionHover(optionData)
      }
    }
  }, [])

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      lastMousePositionRef.current = { x: e.clientX, y: e.clientY }
      if (!isKeyboardNavigatingRef.current && hoveredOptionRef.current) {
        const target = e.target as Element | null
        if (!target?.closest?.('[role="listbox"], [role="menu"], [role="option"], div[id*="-option-"]')) {
          hoveredOptionRef.current = null
        }
      }
    }

    const handleScrollOrWheel = () => {
      isKeyboardNavigatingRef.current = false
      if (lastMousePositionRef.current && typeof document !== 'undefined') {
        const el = document.elementFromPoint(
          lastMousePositionRef.current.x,
          lastMousePositionRef.current.y
        )
        const optionEl = el?.closest?.('[role="option"], div[id*="-option-"]')
        if (optionEl && internalSelectRef.current) {
          const focusable = internalSelectRef.current.getFocusableOptionsWithIds?.() || []
          const found = focusable.find((o: any) => o.id === optionEl.id)
          if (found) {
            hoveredOptionRef.current = found.data
            internalSelectRef.current.blockOptionHover = false
            internalSelectRef.current.onOptionHover?.(found.data)
          }
        }
      }
    }

    window.addEventListener('mousemove', handleMouseMove, { passive: true })
    window.addEventListener('scroll', handleScrollOrWheel, { capture: true, passive: true })
    window.addEventListener('wheel', handleScrollOrWheel, { passive: true })

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('scroll', handleScrollOrWheel, { capture: true })
      window.removeEventListener('wheel', handleScrollOrWheel)
    }
  }, [])

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (
      event.key === 'ArrowDown' ||
      event.key === 'ArrowUp' ||
      event.key === 'PageDown' ||
      event.key === 'PageUp' ||
      event.key === 'Home' ||
      event.key === 'End'
    ) {
      isKeyboardNavigatingRef.current = true
      hoveredOptionRef.current = null
    }

    if ((event.key === ' ' || event.code === 'Space') && !props.inputValue) {
      const instance = internalSelectRef.current
      if (instance && instance.state?.menuIsOpen) {
        let optionToSelect = !isKeyboardNavigatingRef.current ? hoveredOptionRef.current : null

        if (!optionToSelect && !isKeyboardNavigatingRef.current && typeof document !== 'undefined') {
          const hoveredEl = document.querySelector('[role="option"]:hover, div[id*="-option-"]:hover')
          if (hoveredEl) {
            const elId = hoveredEl.id
            const focusable = instance.getFocusableOptionsWithIds?.() || []
            const found = focusable.find((o: any) => o.id === elId)
            if (found) {
              optionToSelect = found.data
            }
          }
        }

        if (optionToSelect) {
          event.preventDefault()
          instance.selectOption(optionToSelect)
          instance.blockOptionHover = false
          instance.onOptionHover?.(optionToSelect)
          props.onKeyDown?.(event)
          return
        }
      }
    }

    props.onKeyDown?.(event)
  }

  const getOptionKey = (item: any, formatted?: any) => {
    if (item && typeof item === 'object') {
      return item.value ?? formatted?.value ?? item.id ?? item.member_code ?? item.key ?? JSON.stringify(item)
    }
    return item
  }

  const formattedOptions = useMemo(() => {
    if (!options) return undefined
    return options.map((item) => {
      const formatted = selectOptionRef.current ? selectOptionRef.current(item as any) : {}
      const key = getOptionKey(item, formatted)
      const cached = optionsMapRef.current.get(key)
      if (cached) {
        Object.assign(cached, formatted, item)
        return cached
      }
      const result = { ...formatted, ...item }
      optionsMapRef.current.set(key, result)
      return result
    })
  }, [options])

  const formattedValue = useMemo(() => {
    try {
      if (Array.isArray(value)) {
        return value.map((item) => {
          if (!item) return item
          const itemVal = (item as any)?.value ?? (item as any)?.id ?? item
          const found = formattedOptions?.find((o: any) => ((o?.value ?? o?.id ?? o) === itemVal))
          if (found) return found

          const formatted = selectOptionRef.current ? selectOptionRef.current(item as any) : {}
          const key = getOptionKey(item, formatted)
          const cached = optionsMapRef.current.get(key)
          if (cached) {
            Object.assign(cached, formatted, item)
            return cached
          }
          if (typeof item === 'object' && 'label' in item && 'value' in item) return item
          const result = { ...formatted, ...item }
          optionsMapRef.current.set(key, result)
          return result
        })
      } else {
        if (typeof value === 'string' || typeof value === 'number') {
          const found = formattedOptions?.find((o: any) => (o?.value ?? o) === value)
          if (found) {
            return found
          }
        }
        if (!!value && typeof value === 'object' && Object.values(value).length > 0) {
          const itemVal = (value as any)?.value ?? (value as any)?.id ?? value
          const found = formattedOptions?.find((o: any) => ((o?.value ?? o?.id ?? o) === itemVal))
          if (found) {
            return found
          }

          const formatted = selectOptionRef.current ? selectOptionRef.current(value as any) : {}
          const key = getOptionKey(value, formatted)
          const cached = optionsMapRef.current.get(key)
          if (cached) {
            Object.assign(cached, formatted, value)
            return cached
          }
          if ('label' in value && 'value' in value) return value
          const result = { ...formatted, ...value }
          optionsMapRef.current.set(key, result)
          return result
        }
        if (value === null) {
          return null
        }
      }
    } catch (error) {
    }
    return undefined
  }, [value, formattedOptions])

  return (
    <div className={twMerge('relative', className)}>
      {(!!label) && <Label required={props.required} {...{ error }}>{label}</Label>}
      <InputSelect
        components={Components as any}
        isSearchable={true}
        hideSelectedOptions={!props.isMulti}
        closeMenuOnSelect={!props.isMulti}
        isDisabled={disabled}
        onChange={(_: any) => onChangeValue?.(_)}
        options={formattedOptions}
        ref={handleSelectRef}
        onKeyDown={handleKeyDown}
        {...({ onOptionHoverCustom } as any)}
        onMenuClose={() => {
          hoveredOptionRef.current = null
          props.onMenuClose?.()
        }}
        classNames={{
          placeholder: () => twMerge(
            Theme.placeholder,
            variant && SelectVariants[variant].placeholder,
          ),
          control: (_) => twMerge(
            '!border-transparent min-w-0',
            error && Theme.controlError,
            error && _.isFocused && Theme.controlErrorFocus,
            variant && SelectVariants[variant].control.default,
            value && variant && SelectVariants[variant].control.selected,
          ),
          valueContainer: (_) => twMerge(
            Theme.valueContainer,
            _.isMulti ? 'py-1 flex-wrap' : 'py-2.5 flex-nowrap overflow-hidden',
          ),
          singleValue: () => twMerge(
            Theme.singleValue,
            value && variant && SelectVariants[variant].singleValue,
          ),
          multiValue: (_) => twMerge(Theme.multiValue),
          multiValueLabel: (_) => twMerge(Theme.multiValueLabel),
          input: () => twMerge(Theme.input),
          clearIndicator: () => twMerge(variant && SelectVariants[variant].clearIndicator),
          indicatorsContainer: () => twMerge(Theme.indicatorsContainer),
          option: (_) => twMerge(
            Theme.option,
            _.isFocused && Theme.optionFocus,
            _.isDisabled && Theme.optionDisabled
          ),
          menuList: () => twMerge(Theme.menuList),
          menu: () => twMerge(Theme.menu),
        }}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        value={formattedValue}
        styles={{
          control: ({
            backgroundColor,
            borderRadius,
            borderColor,
            borderStyle,
            boxShadow,
            outline,
            ...base
          }) => ({
            ...base,
            borderWidth: 1,
            minHeight: 40,
            minWidth: 0,
          }),
          valueContainer: (base, state) => {
            const isMulti = state?.isMulti ?? props.isMulti
            return {
              ...base,
              minWidth: 0,
              flexWrap: isMulti ? 'wrap' : 'nowrap',
              ...(!isMulti ? { overflow: 'hidden' } : {}),
            }
          },
          multiValue: (base) => ({
            ...base,
            flexShrink: 0,
            maxWidth: '100%',
          }),
          placeholder: (base) => ({
            ...base,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '100%',
          }),
          singleValue: (base) => ({
            ...base,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '100%',
          }),
          input: (base) => ({
            ...base,
            minWidth: 0,
          }),
          option: (base, state) => ({
            ...base,
            color: state.isSelected ? 'text-primary' : '',
            backgroundColor: state.isFocused ? '#EFF6FF' : '',
          }),
          menuPortal: base => ({ ...base, zIndex: 999999 }),
        }}
        key={selectKey}
        {...props}
      />
      {(!!helperText) && <HelperText {...{ error }}>{helperText}</HelperText>}
    </div>
  )
}

export const SelectAsyncPaginate = withAsyncPaginate(Select)